import React from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { PRODUCT_CATEGORIES } from '../data/mock';
import { Sun, Battery, Car, Thermometer, Wind, ArrowRight, Zap } from 'lucide-react';

const iconMap = {
  Sun: Sun,
  Battery: Battery,
  Car: Car,
  Thermometer: Thermometer,
  Wind: Wind,
};

export const ProductCategories = ({ onCompare }) => {
  return (
    <section id="products" className="py-16 md:py-20 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-orange-100 px-4 py-2 rounded-full mb-4">
            <Zap className="w-4 h-4 text-cyo-orange" />
            <span className="text-sm font-semibold text-cyo-orange">Home Electrification Products</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Everything You Need to{' '}
            <span className="text-cyo-orange">Electrify Your Home</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Compare the full range of home electrification products. From solar panels to EV chargers, 
            find the best deals to power your home with clean, efficient electricity.
          </p>
        </div>

        {/* Products Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
          {PRODUCT_CATEGORIES.map((category, index) => {
            const Icon = iconMap[category.icon];
            if (!Icon) return null;
            
            // Color scheme for each product
            const colors = {
              solar: { bg: 'bg-amber-50', border: 'border-amber-200', icon: 'bg-amber-100 text-amber-600' },
              battery: { bg: 'bg-blue-50', border: 'border-blue-200', icon: 'bg-blue-100 text-blue-600' },
              'ev-charger': { bg: 'bg-green-50', border: 'border-green-200', icon: 'bg-green-100 text-green-600' },
              'heat-pump': { bg: 'bg-red-50', border: 'border-red-200', icon: 'bg-red-100 text-red-600' },
              hvac: { bg: 'bg-cyan-50', border: 'border-cyan-200', icon: 'bg-cyan-100 text-cyan-600' },
            };
            
            const colorScheme = colors[category.id] || { bg: 'bg-gray-50', border: 'border-gray-200', icon: 'bg-gray-100 text-gray-600' };
            
            return (
              <Card
                key={category.id}
                className={`group ${colorScheme.bg} border ${colorScheme.border} hover:border-cyo-orange rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden hover:-translate-y-1`}
                onClick={() => onCompare(category.id)}
              >
                <CardContent className="p-5">
                  {/* Step number badge */}
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-bold text-gray-400">STEP {index + 1}</span>
                    <div className={`w-8 h-8 ${colorScheme.icon} rounded-lg flex items-center justify-center`}>
                      <Icon className="w-4 h-4" />
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="text-lg font-bold text-gray-900 mb-1.5 group-hover:text-cyo-orange transition-colors duration-300">
                    {category.name}
                  </h3>

                  {/* Description */}
                  <p className="text-gray-500 text-sm mb-3 line-clamp-2">
                    {category.description}
                  </p>

                  {/* Benefit tag */}
                  <div className="bg-white/80 text-gray-700 text-xs font-medium px-2.5 py-1.5 rounded-lg inline-flex items-center gap-1 mb-4 border border-gray-200">
                    <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                    {category.benefit}
                  </div>

                  {/* CTA */}
                  <Button
                    variant="ghost"
                    className="w-full justify-between text-cyo-orange hover:text-white hover:bg-cyo-orange font-semibold text-sm rounded-xl group/btn transition-all duration-300"
                  >
                    Compare Now
                    <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Bottom message */}
        <div className="text-center mt-10">
          <p className="text-gray-500 text-sm">
            <span className="font-semibold text-gray-700">Pro tip:</span> Start with solar, then add battery storage, 
            and replace gas appliances one at a time for the best savings.
          </p>
        </div>
      </div>
    </section>
  );
};
